import { useEffect, useRef, useState } from 'react';
import { Send, X, RefreshCw, Sparkles } from 'lucide-react';
import { api } from '../lib/api.js';
import { useAuth } from '../lib/auth.jsx';
import { toastError } from '../lib/errors.js';

const AVATAR = '/connecta.png';

// Ações sugeridas — mapeiam para as tools read-only do backend (módulo ia).
// `fill: true` apenas preenche o input (espera o usuário completar com um nome).
const SUGESTOES = [
  { label: 'DRE do mês', prompt: 'Me dê o resumo do DRE consolidado do mês atual (referência hoje): receita, despesas e lucro líquido.' },
  { label: 'Inadimplência', prompt: 'Quais clientes estão inadimplentes e qual o total devido?' },
  { label: 'Resumo de cadastros', prompt: 'Quantos clientes, fornecedores e colaboradores ativos nós temos?' },
  { label: 'Buscar cliente', prompt: 'Faça um resumo 360 do cliente ', fill: true },
];

const SAUDACAO = {
  role: 'assistant',
  content:
    'Oi! Eu sou a Connecta, sua assistente do Feed ERP. Posso consultar o DRE, inadimplência, ' +
    'resumo de cadastros e dados de clientes. Como posso ajudar?',
};

export default function ConnectaWidget() {
  const { user, level } = useAuth();
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([SAUDACAO]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [configurado, setConfigurado] = useState(null);
  const inputRef = useRef(null);
  const scrollRef = useRef(null);

  // Visível só para admin (a rota /ia é admin-only durante a fase de testes).
  const podeUsar = !!user && level === 'admin';

  useEffect(() => {
    if (!open || configurado !== null) return;
    api
      .iaStatus()
      .then((res) => setConfigurado(!!res?.data?.configurado))
      .catch(() => setConfigurado(false));
  }, [open, configurado]);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, sending, open]);

  useEffect(() => {
    if (open && inputRef.current) inputRef.current.focus();
  }, [open]);

  if (!podeUsar) return null;

  async function enviar(texto) {
    const msg = (texto ?? input).trim();
    if (!msg || sending) return;

    const novaUser = { role: 'user', content: msg };
    const historico = messages
      .filter((m) => m.role === 'user' || m.role === 'assistant')
      .slice(-12)
      .map((m) => ({ role: m.role, content: m.content }));

    setMessages((prev) => [...prev, novaUser]);
    setInput('');
    setSending(true);
    try {
      const res = await api.iaChat(msg, historico);
      const resposta = res?.data?.resposta || 'Não consegui responder agora. Tente reformular.';
      setMessages((prev) => [...prev, { role: 'assistant', content: resposta }]);
    } catch (e) {
      toastError(e, 'consultar a Connecta');
      setMessages((prev) => [
        ...prev,
        { role: 'assistant', content: 'Tive um problema para responder. Verifique a configuração da IA e tente de novo.' },
      ]);
    } finally {
      setSending(false);
    }
  }

  function onChip(s) {
    if (s.fill) {
      setInput(s.prompt);
      inputRef.current?.focus();
    } else {
      enviar(s.prompt);
    }
  }

  function onKeyDown(e) {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      enviar();
    }
  }

  function limpar() {
    setMessages([SAUDACAO]);
  }

  return (
    <>
      {/* Botão flutuante */}
      {!open && (
        <button
          type="button"
          onClick={() => setOpen(true)}
          className="fixed bottom-5 right-5 z-[900] flex items-center gap-2 rounded-full bg-surface-solid border border-border shadow-lg pl-1.5 pr-4 py-1.5 hover:border-accent transition-colors group"
          title="Falar com a Connecta"
        >
          <img src={AVATAR} alt="Connecta" className="h-9 w-9 rounded-full object-cover ring-2 ring-accent" />
          <span className="text-sm font-600 text-text">Connecta</span>
          <Sparkles size={14} className="text-accent group-hover:scale-110 transition-transform" />
        </button>
      )}

      {/* Painel de chat */}
      {open && (
        <div className="fixed bottom-5 right-5 z-[900] flex flex-col w-[min(92vw,380px)] h-[min(80vh,560px)] rounded-xl border border-border bg-surface-solid shadow-2xl overflow-hidden">
          {/* Header */}
          <div className="flex items-center gap-3 px-4 py-3 border-b border-border bg-accent-soft">
            <img src={AVATAR} alt="Connecta" className="h-10 w-10 rounded-full object-cover ring-2 ring-accent" />
            <div className="min-w-0 flex-1">
              <div className="text-sm font-700 text-text leading-tight">Connecta</div>
              <div className="text-xs text-text-muted leading-tight">Assistente do Feed ERP</div>
            </div>
            <button type="button" className="btn-secondary text-xs p-1.5" onClick={limpar} title="Limpar conversa">
              <RefreshCw size={14} />
            </button>
            <button type="button" className="btn-secondary text-xs p-1.5" onClick={() => setOpen(false)} title="Fechar">
              <X size={14} />
            </button>
          </div>

          {/* Mensagens */}
          <div ref={scrollRef} className="flex-1 overflow-y-auto px-3 py-3 space-y-3">
            {configurado === false && (
              <div className="text-xs text-danger bg-danger/10 border border-danger/30 rounded-lg px-3 py-2">
                IA não configurada. Defina a chave OpenAI no ambiente para conversar.
              </div>
            )}
            {messages.map((m, i) => (
              <div key={i} className={m.role === 'user' ? 'flex justify-end' : 'flex justify-start'}>
                <div
                  className={[
                    'max-w-[85%] rounded-xl px-3 py-2 text-sm whitespace-pre-wrap break-words',
                    m.role === 'user'
                      ? 'bg-accent text-white rounded-br-sm'
                      : 'bg-surface-hover text-text rounded-bl-sm',
                  ].join(' ')}
                >
                  {m.content}
                </div>
              </div>
            ))}
            {sending && (
              <div className="flex justify-start">
                <div className="bg-surface-hover text-text-muted rounded-xl rounded-bl-sm px-3 py-2 text-sm">
                  Connecta está pensando…
                </div>
              </div>
            )}
          </div>

          {/* Sugestões */}
          {messages.length <= 1 && (
            <div className="px-3 pb-2 flex flex-wrap gap-1.5">
              {SUGESTOES.map((s) => (
                <button
                  key={s.label}
                  type="button"
                  onClick={() => onChip(s)}
                  disabled={sending}
                  className="text-xs px-2.5 py-1 rounded-full border border-border text-text-muted hover:border-accent hover:text-text transition-colors disabled:opacity-50"
                >
                  {s.label}
                </button>
              ))}
            </div>
          )}

          {/* Input */}
          <div className="border-t border-border p-2 flex items-end gap-2">
            <textarea
              ref={inputRef}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={onKeyDown}
              rows={1}
              placeholder="Pergunte algo…"
              className="flex-1 resize-none max-h-24 bg-surface border border-border rounded-lg px-3 py-2 text-sm text-text placeholder:text-text-muted focus:outline-none focus:border-accent"
            />
            <button
              type="button"
              onClick={() => enviar()}
              disabled={sending || !input.trim()}
              className="btn-secondary p-2 shrink-0 disabled:opacity-50"
              title="Enviar"
            >
              <Send size={16} />
            </button>
          </div>
        </div>
      )}
    </>
  );
}
